import { useEffect, useRef } from "react";
import { LayersConfig } from "../../AppConfig";
import CancelIcon from "../../assets/cancel.svg?react";
import DoneIcon from "../../assets/done.svg?react";
import LighthouseIcon from "../../assets/lighthouse.svg?react";
import LocationPinIcon from "../../assets/location-pin.svg?react";
import MoveIcon from "../../assets/move.svg?react";
import { zoomTo } from "../../utils/arcgisUtils";
import { isLighthouse } from "../../utils/lighthouseUtils";
import NfsButton from "../genericComponents/NfsButton/NfsButton";
import "./FeatureViewerTitle.css";
import { formatDate } from "../../utils/helpers";
import type { ResourceHandle } from "@arcgis/core/core/Handles";
import type Graphic from "@arcgis/core/Graphic";

interface FeatureViewerTitleProps {
    symbolHtmlElement: HTMLElement | null;
    feature: Graphic;
    layerConfig: React.RefObject<LayersConfig | null | undefined>;
    goTo?: boolean;
    editingGeometry?: boolean;
    editGeometry?: () => void;
    isEditing?: boolean;
    saveGeometry?: () => void;
    cancelGeometry?: () => void;
    canMove: boolean;
}

const FeatureViewerTitle = (props: FeatureViewerTitleProps) => {
    const {
        symbolHtmlElement,
        feature,
        layerConfig,
        goTo,
        editingGeometry,
        editGeometry,
        isEditing,
        saveGeometry,
        cancelGeometry,
        canMove,
    } = props;


    const getFeatureTitle = () => {
        if (layerConfig.current?.TitleField === "malt_dato") {
            const dateValue = feature.attributes[layerConfig.current?.TitleField];
            if (dateValue) {
                const formattedDate = formatDate(dateValue);
                return formattedDate;
            }
        }
        return feature.attributes[layerConfig.current?.TitleField ?? "objectid"];
    }

    const highlightHandlesRef = useRef<ResourceHandle[]>([]);

    const isOverrett = () => {
        if (!feature) return false;
        if (feature.layer?.id === "Overettlinje lys" || feature.layer?.id === "Overettlinje merke") {
            return true;
        }
        return false;
    };

    const goToFeature = () => {
        zoomTo(feature, storeHighlightHandle);
    }

    const clearHighlightedFeatures = () => {
        highlightHandlesRef.current.forEach((handle) => {
            handle.remove();
        });
        highlightHandlesRef.current = [];
    };

    const storeHighlightHandle = (handle: ResourceHandle | null) => {
        clearHighlightedFeatures();
        if (handle) {
            highlightHandlesRef.current.push(handle);
        }
    };

    useEffect(() => {
        clearHighlightedFeatures();
        return () => {
            clearHighlightedFeatures();
        };
    }, []);

    return (
        <div className="feature-viewer-feature-title">
            <div className="flex-row center small-gap feature-title-details">
                {isLighthouse(feature) ? (
                    <LighthouseIcon />
                ) : (
                    <div
                        className="search-item-symbol"
                        dangerouslySetInnerHTML={{ __html: symbolHtmlElement?.outerHTML ?? "" }}
                    ></div>
                )}
                <span>{feature.attributes["sjomerke_nr"] ?? "Objid:" + feature.attributes["objectid"]}</span>
                <span className="feature-title-name" title={getFeatureTitle()?.toString() || ""}>
                    {getFeatureTitle()}
                </span>
            </div>
            <div className="flex-row center small-gap">
                {canMove && isEditing && !editingGeometry && editGeometry && (
                    <NfsButton round inverted outlined className="no-flex padding-0" onClick={editGeometry}>
                        <MoveIcon style={{ width: "24px", height: "24px" }} />
                    </NfsButton>
                )}
                {canMove && isEditing && !isOverrett() && saveGeometry && cancelGeometry && editingGeometry && (
                    <>
                        <NfsButton round className="no-flex move-done-button" onClick={saveGeometry}>
                            <DoneIcon />
                        </NfsButton>
                        <NfsButton round inverted outlined className="no-flex" onClick={cancelGeometry}>
                            <CancelIcon />
                        </NfsButton>
                    </>
                )}
                {goTo && (
                    <NfsButton disabled={!feature.geometry} onClick={goToFeature} small empty>
                        <LocationPinIcon />
                    </NfsButton>
                )}
            </div>
        </div>
    );
};

export default FeatureViewerTitle;
